// Square-crop dialog for tournament covers. Wraps react-easy-crop (drag + zoom,
// touch-friendly for mobile organisers) locked to a 1:1 aspect, and emits a
// COVER_SIZE×COVER_SIZE JPEG blob via `onCropped`. The component is controlled by
// `open` + `imageSrc`; the parent owns the source object URL and its lifecycle.

import { useCallback, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Slider from '@mui/material/Slider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import Cropper from 'react-easy-crop';
import type { Area, Point } from 'react-easy-crop';
import { COVER_SIZE } from '../domain';
import { getCroppedCoverBlob } from './cropImage';
import { useTranslation } from '../state/I18nContext';

interface CoverCropDialogProps {
  open: boolean;
  imageSrc: string;
  onCancel: () => void;
  onCropped: (blob: Blob) => void;
}

export function CoverCropDialog({
  open,
  imageSrc,
  onCancel,
  onCropped,
}: CoverCropDialogProps) {
  const { t } = useTranslation();
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [areaPixels, setAreaPixels] = useState<Area | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onCropComplete = useCallback((_: Area, pixels: Area) => {
    setAreaPixels(pixels);
  }, []);

  const confirm = async () => {
    if (!areaPixels) return;
    setBusy(true);
    setError(null);
    try {
      const blob = await getCroppedCoverBlob(imageSrc, areaPixels);
      onCropped(blob);
    } catch (e: unknown) {
      setError(
        e instanceof Error ? e.message : t('admin.cover.crop.cropFailed'),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onCancel}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>{t('admin.cover.crop.title')}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('admin.cover.crop.body', { size: COVER_SIZE })}
        </Typography>
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: 320,
            bgcolor: 'black',
            borderRadius: 1,
            overflow: 'hidden',
          }}
        >
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={1}
            cropShape="rect"
            showGrid
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
          />
        </Box>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 2 }}>
          <ZoomOutIcon fontSize="small" color="action" />
          <Slider
            aria-label={t('admin.cover.crop.zoom')}
            min={1}
            max={3}
            step={0.01}
            value={zoom}
            onChange={(_, value) => setZoom(value as number)}
          />
          <ZoomInIcon fontSize="small" color="action" />
        </Stack>
        {error && (
          <Typography color="error" variant="body2" sx={{ mt: 1 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={busy}>
          {t('admin.common.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={() => void confirm()}
          disabled={busy || !areaPixels}
        >
          {busy ? t('admin.cover.crop.saving') : t('admin.cover.crop.useCover')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
